import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiCheckCircle, FiAlertCircle, FiLoader, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';

interface ImportProgressDialogProps {
  batchId: string;
  onClose: () => void;
  onComplete?: () => void;
}

interface BatchJobStatus {
  id: string;
  status: string;
  total_items: number;
  processed_items: number;
  error_count: number;
  error_details?: any;
}

export const ImportProgressDialog: React.FC<ImportProgressDialogProps> = ({
  batchId,
  onClose,
  onComplete
}) => {
  const { currentBusiness, isCookieMode, token, API_BASE_URL } = useAuth();
  const [jobStatus, setJobStatus] = useState<BatchJobStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const pollStatus = useCallback(async () => {
    try {
      if (!currentBusiness) {
        throw new Error('No business selected');
      }

      // Cache-buster to avoid 304 from any intermediary caching layers
      const url = `${API_BASE_URL}/batch/${batchId}?t=${Date.now()}`;
      const response = await fetch(url, {
        headers: {
          ...(isCookieMode ? {} : { Authorization: `Bearer ${token}` }),
        },
        credentials: isCookieMode ? 'include' : 'omit',
      });

      // Treat 304 Not Modified as a non-fatal state: keep prior status and retry
      if (response.status === 304) {
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch job status: ${response.status}`);
      }

      const status: BatchJobStatus = await response.json();
      setJobStatus(status);
      setIsLoading(false);
      setError(null);

      // Check if job is complete
      if (['completed', 'completed_with_errors', 'failed'].includes(status.status)) {
        if (onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      console.error('Error polling batch job status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch status';
      setError(errorMessage);
      setIsLoading(false);
      
      // Retry logic for network errors
      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          setError(null);
          setIsLoading(true);
        }, 2000);
      }
    }
  }, [batchId, retryCount, onComplete, currentBusiness]);

  useEffect(() => {
    // Poll immediately
    pollStatus();

    // Set up polling interval
    const interval = setInterval(pollStatus, 1000); // Poll every second

    return () => clearInterval(interval);
  }, [pollStatus]);

  const getStatusIcon = () => {
    if (isLoading) return <FiLoader className="w-6 h-6 animate-spin" />;
    
    switch (jobStatus?.status) {
      case 'completed':
        return <FiCheckCircle className="w-6 h-6 text-green-500" />;
      case 'completed_with_errors':
        return <FiAlertCircle className="w-6 h-6 text-yellow-500" />;
      case 'failed':
        return <FiAlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <FiLoader className="w-6 h-6 animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (isLoading) return 'Checking status...';
    
    switch (jobStatus?.status) {
      case 'queued':
        return 'Job queued...';
      case 'pending':
        return 'Job queued...';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Task complete';
      case 'completed_with_errors':
        return `Task complete (with ${jobStatus?.error_count || 0} errors)`;
      case 'failed':
        return 'Failed';
      default:
        return `Status: ${jobStatus?.status || 'Unknown'}`;
    }
  };

  const getProgressPercentage = () => {
    if (!jobStatus || jobStatus.total_items === 0) return 0;
    return Math.round((jobStatus.processed_items / jobStatus.total_items) * 100);
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setIsLoading(true);
  };

  const isFinal = !!jobStatus && ['completed', 'completed_with_errors', 'failed'].includes(jobStatus.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Import Progress
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {error ? (
          <div className="space-y-4">
            <div className="text-red-500 mb-4">
              <FiAlertCircle className="w-5 h-5 inline mr-2" />
              {error}
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleRetry}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <FiRefreshCw className="w-4 h-4 inline mr-2" />
                Retry
              </button>
              <button
                onClick={onClose}
                className="px-3 py-2 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              {getStatusIcon()}
              <span className="text-gray-700 dark:text-gray-300">
                {getStatusText()}
              </span>
            </div>

            {jobStatus && (
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>

                {/* Progress Details */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span>Processed:</span>
                    <span>{jobStatus.processed_items} / {jobStatus.total_items}</span>
                  </div>
                  {jobStatus.error_count > 0 && (
                    <div className="flex justify-between">
                      <span>Errors:</span>
                      <span className="text-red-600">{jobStatus.error_count}</span>
                    </div>
                  )}
                </div>

                {/* Error Details */}
                {jobStatus.error_details && (
                  <div className="mt-4">
                    {jobStatus.error_details.generalError && (
                      <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex items-center">
                          <FiAlertCircle className="w-4 h-4 text-red-500 mr-2" />
                          <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                            {jobStatus.error_details.generalError}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {jobStatus.error_details.rowErrors && jobStatus.error_details.rowErrors.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Row-Specific Errors:
                        </h4>
                        <div className="max-h-32 overflow-y-auto text-xs text-red-600">
                          {jobStatus.error_details.rowErrors.slice(0, 10).map((error: any, index: number) => (
                            <div key={index} className="mb-1">
                              <strong>Row {error.row}:</strong> {error.customer ? `${error.customer} - ` : ''}{error.reason}
                            </div>
                          ))}
                          {jobStatus.error_details.rowErrors.length > 10 && (
                            <div className="text-gray-500">
                              ... and {jobStatus.error_details.rowErrors.length - 10} more errors
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!error && isFinal && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Close window
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 